/**
 * Native JavaScript replacements for lodash functions
 * These implementations provide the same functionality without the bundle size overhead
 */

/**
 * Rounds a number to the specified precision
 * @param value The number to round
 * @param precision The number of decimal places (default 0)
 */
export function round(value: number, precision: number = 0): number {
  const factor = Math.pow(10, precision);
  return Math.round(value * factor) / factor;
}

/**
 * Calculates the sum of an array of numbers
 * @param array Array of numbers to sum
 */
export function sum(array: number[] | undefined | null): number {
  if (!array || array.length === 0) return 0;
  return array.reduce((acc, val) => acc + val, 0);
}

/**
 * Sorts an array of objects by multiple properties
 * @param collection The array to sort
 * @param iteratees Array of property names or functions to sort by
 * @param orders Array of 'asc' or 'desc' for each property
 */
export function orderBy<T>(
  collection: T[],
  iteratees: (keyof T | ((item: T) => any))[],
  orders: ('asc' | 'desc')[] = []
): T[] {
  if (!collection || collection.length === 0) return [];
  
  const sorted = [...collection];
  
  sorted.sort((a, b) => {
    for (let i = 0; i < iteratees.length; i++) {
      const iteratee = iteratees[i];
      const order = orders[i] || 'asc';
      
      let aVal: any;
      let bVal: any;
      
      if (typeof iteratee === 'function') {
        aVal = iteratee(a);
        bVal = iteratee(b);
      } else {
        aVal = a[iteratee];
        bVal = b[iteratee];
      }
      
      // Handle null/undefined
      if (aVal == null && bVal == null) continue;
      if (aVal == null) return order === 'asc' ? 1 : -1;
      if (bVal == null) return order === 'asc' ? -1 : 1;
      
      // Compare values
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
    }
    return 0;
  });
  
  return sorted;
}

/**
 * Creates an array of unique values based on a property
 * @param array The array to process
 * @param iteratee The property name to check for uniqueness
 */
export function uniqBy<T>(array: T[], iteratee: keyof T | ((item: T) => any)): T[] {
  if (!array || array.length === 0) return [];
  
  const seen = new Set();
  const result: T[] = [];
  
  for (const item of array) {
    const key = typeof iteratee === 'function' ? iteratee(item) : item[iteratee];
    if (!seen.has(key)) {
      seen.add(key);
      result.push(item);
    }
  }
  
  return result;
}

/**
 * Creates an array of unique values
 * @param array The array to process
 */
export function uniq<T>(array: T[]): T[] {
  return [...new Set(array)];
}

/**
 * Creates an array of numbers from start to end (exclusive)
 * @param start The start of the range
 * @param end The end of the range (exclusive)
 * @param step The value to increment by (default 1)
 */
export function range(start: number, end?: number, step: number = 1): number[] {
  if (end === undefined) {
    end = start;
    start = 0;
  }
  
  const length = Math.max(Math.ceil((end - start) / step), 0);
  const result = new Array(length);
  
  for (let i = 0; i < length; i++) {
    result[i] = start + (i * step);
  }
  
  return result;
}

/**
 * Gets a random element from an array
 * @param array The array to sample from
 */
export function sample<T>(array: T[]): T | undefined {
  if (!array || array.length === 0) return undefined;
  return array[Math.floor(Math.random() * array.length)];
}

/**
 * Creates an object composed of the picked properties
 * @param object The source object
 * @param keys The property keys to pick
 */
export function pick<T extends object, K extends keyof T>(
  object: T,
  keys: K[]
): Pick<T, K> {
  const result = {} as Pick<T, K>;
  
  for (const key of keys) {
    if (key in object) {
      result[key] = object[key];
    }
  }
  
  return result;
}

/**
 * Computes the mean of an array of numbers
 * @param array Array of numbers
 */
export function mean(array: number[]): number {
  if (!array || array.length === 0) return 0;
  return sum(array) / array.length;
}

/**
 * Returns the value if it's not null/undefined, otherwise returns the default value
 * @param value The value to check
 * @param defaultValue The default value
 */
export function defaultTo<T>(value: T | null | undefined, defaultValue: T): T {
  return value == null ? defaultValue : value;
}

/**
 * Debounced function type with cancel and flush methods
 */
export interface DebouncedFunc<T extends (...args: any[]) => any> {
  (...args: Parameters<T>): void;
  cancel(): void;
  flush(): void;
}

/**
 * Creates a debounced function that delays invoking func until after wait milliseconds
 * have elapsed since the last time the debounced function was invoked
 * @param func The function to debounce
 * @param wait The number of milliseconds to delay
 * @param options Options object with leading, trailing, and maxWait properties
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number = 0,
  options?: {
    leading?: boolean;
    trailing?: boolean;
    maxWait?: number;
  }
): DebouncedFunc<T> {
  let timerId: ReturnType<typeof setTimeout> | undefined;
  let lastArgs: Parameters<T> | undefined;
  let lastThis: any;
  let lastCallTime: number | undefined;
  let lastInvokeTime = 0;

  const leading = options?.leading ?? false;
  const trailing = options?.trailing ?? true;
  const maxWait = options?.maxWait;

  function invokeFunc(time: number) {
    const args = lastArgs!;
    const thisArg = lastThis;

    lastArgs = lastThis = undefined;
    lastInvokeTime = time;
    return func.apply(thisArg, args);
  }

  function shouldInvoke(time: number): boolean {
    const timeSinceLastCall = lastCallTime === undefined ? 0 : time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;

    // Either this is the first call, or enough time has elapsed
    return (
      lastCallTime === undefined ||
      timeSinceLastCall >= wait ||
      timeSinceLastCall < 0 ||
      (maxWait !== undefined && timeSinceLastInvoke >= maxWait)
    );
  }

  function timerExpired() {
    const time = Date.now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    // Restart timer
    const timeSinceLastCall = lastCallTime === undefined ? 0 : time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;
    const timeWaiting = wait - timeSinceLastCall;
    const remainingWait = maxWait !== undefined
      ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke)
      : timeWaiting;

    timerId = setTimeout(timerExpired, remainingWait);
  }

  function leadingEdge(time: number) {
    lastInvokeTime = time;
    timerId = setTimeout(timerExpired, wait);
    return leading ? invokeFunc(time) : undefined;
  }

  function trailingEdge(time: number) {
    timerId = undefined;

    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = lastThis = undefined;
    return undefined;
  }

  function cancel() {
    if (timerId !== undefined) {
      clearTimeout(timerId);
    }
    lastInvokeTime = 0;
    lastArgs = lastThis = lastCallTime = timerId = undefined;
  }

  function flush() {
    return timerId === undefined ? undefined : trailingEdge(Date.now());
  }

  function debounced(this: any, ...args: Parameters<T>) {
    const time = Date.now();
    const isInvoking = shouldInvoke(time);

    lastArgs = args;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timerId === undefined) {
        return leadingEdge(lastCallTime);
      }
      if (maxWait !== undefined) {
        // Handle invocations in a tight loop
        timerId = setTimeout(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }
    if (timerId === undefined) {
      timerId = setTimeout(timerExpired, wait);
    }
  }

  debounced.cancel = cancel;
  debounced.flush = flush;

  return debounced as DebouncedFunc<T>;
}

// For backward compatibility with lodash import style
export default {
  round,
  sum,
  orderBy,
  uniqBy,
  uniq,
  range,
  sample,
  pick,
  mean,
  defaultTo,
  debounce
};